/**
 * Scan Item Discovery
 *
 * Pure DOM discovery functions for the unified scan engine.
 * Finds [data-scan-item] elements, filters invisible ones,
 * groups into rows by vertical proximity, and sorts by position.
 */

export interface ScanItem {
  element: HTMLElement;
  zone: 'sidebar' | 'main';
  rect: DOMRect;
}

export interface DiscoveredItems {
  sidebar: ScanItem[][];  // rows of items in sidebar zone
  main: ScanItem[][];     // rows of items in main zone
}

/** Minimum size for an element to be considered visible */
const MIN_SIZE = 10;

function isVisible(el: HTMLElement): boolean {
  // Skip disabled elements
  if ((el as HTMLButtonElement).disabled || el.getAttribute('aria-disabled') === 'true') return false;
  const rect = el.getBoundingClientRect();
  if (rect.width < MIN_SIZE || rect.height < MIN_SIZE) return false;
  const style = window.getComputedStyle(el);
  if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return false;
  // Skip elements fully outside viewport
  if (rect.bottom < 0 || rect.top > window.innerHeight) return false;
  if (rect.right < 0 || rect.left > window.innerWidth) return false;
  return true;
}

function isNestedScanItem(el: HTMLElement): boolean {
  let parent = el.parentElement;
  while (parent) {
    if (parent.hasAttribute('data-scan-item')) return true;
    parent = parent.parentElement;
  }
  return false;
}

function isInSidebarZone(el: HTMLElement): boolean {
  let current: HTMLElement | null = el;
  while (current) {
    if (current.getAttribute('data-scan-zone') === 'sidebar') return true;
    current = current.parentElement;
  }
  return false;
}

/**
 * Find the nearest ancestor with [data-scan-row] and return its value.
 * Returns null if the item has no explicit row marker.
 */
function getScanRowId(el: HTMLElement): string | null {
  let current = el.parentElement;
  while (current) {
    const rowId = current.getAttribute('data-scan-row');
    if (rowId !== null) return rowId;
    current = current.parentElement;
  }
  return null;
}

/**
 * Separate "tall" items (e.g. full-height pagination arrows) from normal
 * content items. A tall item spans multiple visual rows and would break
 * the overlap-based row grouping if left in the normal set.
 *
 * We use a median-based threshold: an item is "tall" if its height exceeds
 * 1.7× the median item height. This adapts to any screen size — the old
 * fixed 120px and viewport-percentage approaches both failed on certain
 * screen configurations.
 */
function separateTallItems(items: ScanItem[]): { normal: ScanItem[]; tall: ScanItem[] } {
  const normal: ScanItem[] = [];
  const tall: ScanItem[] = [];
  if (items.length <= 1) return { normal: [...items], tall: [] };

  // Compute median height
  const heights = items.map(i => i.rect.height).sort((a, b) => a - b);
  const mid = Math.floor(heights.length / 2);
  const median = heights.length % 2 === 1 ? heights[mid] : (heights[mid - 1] + heights[mid]) / 2;

  // Items taller than 1.7× median are "tall" (spanning multiple rows)
  const threshold = median * 1.7;

  for (const item of items) {
    if (item.rect.height > threshold) {
      tall.push(item);
    } else {
      normal.push(item);
    }
  }
  return { normal, tall };
}

/**
 * Minimum vertical overlap ratio (relative to the shorter item) to consider
 * two items in the same visual row. Using overlap instead of center-distance
 * correctly handles CSS grid / flex-wrap rows where items in different rows
 * have zero overlap even if their Y-centers are close.
 */
const OVERLAP_RATIO = 0.5;

/**
 * Group items using explicit [data-scan-row] markers on ancestor elements.
 * Items sharing the same data-scan-row value are in the same row.
 * Items without a data-scan-row ancestor go into an "unmarked" bucket
 * and are grouped by the overlap algorithm as a fallback.
 */
function groupByExplicitRows(items: ScanItem[], isRTL: boolean): ScanItem[][] | null {
  // Check if ANY items have explicit row markers
  const rowMap = new Map<string, ScanItem[]>();
  const unmarked: ScanItem[] = [];

  for (const item of items) {
    const rowId = getScanRowId(item.element);
    if (rowId !== null) {
      let arr = rowMap.get(rowId);
      if (!arr) { arr = []; rowMap.set(rowId, arr); }
      arr.push(item);
    } else {
      unmarked.push(item);
    }
  }

  // If no items have explicit rows, return null to fall back to overlap algorithm
  if (rowMap.size === 0) return null;

  // Build rows sorted by the top edge of their first item (top-to-bottom)
  const explicitEntries = Array.from(rowMap.entries());
  explicitEntries.sort((a, b) => {
    const aTop = Math.min(...a[1].map(i => i.rect.top));
    const bTop = Math.min(...b[1].map(i => i.rect.top));
    return aTop - bTop;
  });

  // Move pagination rows to the end so they scan after content rows
  const contentRows: ScanItem[][] = [];
  const paginationRows: ScanItem[][] = [];
  for (const [rowId, items] of explicitEntries) {
    if (rowId.startsWith('pagination')) {
      paginationRows.push(items);
    } else {
      contentRows.push(items);
    }
  }
  const explicitRows = [...contentRows, ...paginationRows];

  // Sort items within each row by horizontal position
  for (const row of explicitRows) {
    row.sort((a, b) =>
      isRTL ? b.rect.left - a.rect.left : a.rect.left - b.rect.left
    );
  }

  // Unmarked items (action bar, pagination arrows, etc.) are sub-grouped
  // by the overlap algorithm so items at different Y positions don't merge.
  if (unmarked.length > 0) {
    const unmarkedRows = groupByOverlap(unmarked, isRTL);
    // Insert unmarked rows at correct vertical position among explicit rows
    for (const uRow of unmarkedRows) {
      const uTop = Math.min(...uRow.map(i => i.rect.top));
      let insertIdx = explicitRows.length;
      for (let j = 0; j < explicitRows.length; j++) {
        const eTop = Math.min(...explicitRows[j].map(i => i.rect.top));
        if (uTop < eTop) { insertIdx = j; break; }
      }
      explicitRows.splice(insertIdx, 0, uRow);
    }
  }

  return explicitRows;
}

/**
 * Group items into rows by vertical overlap. Items whose bounding rects
 * overlap vertically by at least OVERLAP_RATIO of the shorter item's
 * height are placed in the same row. Rows are sorted top-to-bottom,
 * items within rows by horizontal position.
 */
function groupByOverlap(items: ScanItem[], isRTL: boolean): ScanItem[][] {
  if (items.length === 0) return [];

  const sorted = [...items].sort((a, b) => {
    const topDiff = a.rect.top - b.rect.top;
    if (Math.abs(topDiff) > 1) return topDiff;
    return isRTL ? b.rect.left - a.rect.left : a.rect.left - b.rect.left;
  });

  const rows: ScanItem[][] = [];
  let currentRow: ScanItem[] = [sorted[0]];
  let refTop = sorted[0].rect.top;
  let refBottom = sorted[0].rect.bottom;

  for (let i = 1; i < sorted.length; i++) {
    const item = sorted[i];
    const itemTop = item.rect.top;
    const itemBottom = item.rect.bottom;
    const itemHeight = item.rect.height;
    const refHeight = refBottom - refTop;

    const overlapStart = Math.max(itemTop, refTop);
    const overlapEnd = Math.min(itemBottom, refBottom);
    const overlap = Math.max(0, overlapEnd - overlapStart);
    const minHeight = Math.min(itemHeight, refHeight);

    if (minHeight > 0 && overlap >= minHeight * OVERLAP_RATIO) {
      currentRow.push(item);
    } else {
      rows.push(currentRow);
      currentRow = [item];
      refTop = itemTop;
      refBottom = itemBottom;
    }
  }
  rows.push(currentRow);

  // Sort within rows by horizontal position
  for (const row of rows) {
    row.sort((a, b) =>
      isRTL ? b.rect.left - a.rect.left : a.rect.left - b.rect.left
    );
  }

  return rows;
}

function groupIntoRows(items: ScanItem[], isRTL: boolean): ScanItem[][] {
  if (items.length === 0) return [];

  // Prefer explicit [data-scan-row] grouping when available
  const explicit = groupByExplicitRows(items, isRTL);
  if (explicit) return explicit;

  // Fallback: separate tall items, then group by overlap
  const { normal: normalItems, tall: tallItems } = separateTallItems(items);

  const rows = groupByOverlap(normalItems, isRTL);

  if (tallItems.length > 0) {
    tallItems.sort((a, b) =>
      isRTL ? b.rect.left - a.rect.left : a.rect.left - b.rect.left
    );
    rows.push(tallItems);
  }

  return rows;
}

/**
 * Discover all [data-scan-item] elements, split into sidebar and main zones,
 * grouped into rows by vertical proximity and sorted by horizontal position.
 */
export function discoverScanItems(root: HTMLElement, isRTL: boolean): DiscoveredItems {
  const allElements = root.querySelectorAll<HTMLElement>('[data-scan-item]');

  const sidebarItems: ScanItem[] = [];
  const mainItems: ScanItem[] = [];

  for (const el of Array.from(allElements)) {
    if (!isVisible(el)) continue;
    if (isNestedScanItem(el)) continue;

    const rect = el.getBoundingClientRect();
    const zone = isInSidebarZone(el) ? 'sidebar' : 'main';
    const item: ScanItem = { element: el, zone, rect };

    if (zone === 'sidebar') sidebarItems.push(item);
    else mainItems.push(item);
  }

  const result = {
    sidebar: groupIntoRows(sidebarItems, isRTL),
    main: groupIntoRows(mainItems, isRTL),
  };

  return result;
}

/** Flatten discovered items into a single array: main rows first, then sidebar rows */
export function flattenItems(items: DiscoveredItems): ScanItem[] {
  const flat: ScanItem[] = [];
  for (const row of items.main) {
    for (const item of row) flat.push(item);
  }
  for (const row of items.sidebar) {
    for (const item of row) flat.push(item);
  }
  return flat;
}

/** Find the (zone, rowIndex, itemIndex) position of an element in the discovered items */
export function findItemPosition(
  items: DiscoveredItems,
  el: HTMLElement
): { zone: 'main' | 'sidebar'; rowIndex: number; itemIndex: number } | null {
  for (const zone of ['main', 'sidebar'] as const) {
    const rows = zone === 'main' ? items.main : items.sidebar;
    for (let r = 0; r < rows.length; r++) {
      for (let i = 0; i < rows[r].length; i++) {
        if (rows[r][i].element === el) return { zone, rowIndex: r, itemIndex: i };
      }
    }
  }
  return null;
}
